import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { useUnlimitedOnlyPaywall } from "MVVM/Hooks/useUnlimitedOnlyPaywall";
import { useUser } from "MVVM/Hooks/useUser";

const TRIAL_INELIGIBLE_TIERS = ["CREATOR_PLAT", "INTERNAL"];

/**
 * Single source of truth for whether the current user can start the free
 * Unlimited trial. Mirrors the server-side fence in createTrialSession
 * (allstar-payment-processing):
 * - FREE users get the card-optional trial (no card required).
 * - Paying tiers get the trial with a card — it auto-converts at day 7 — but
 *   only when Stripe owns their billing; a store/Xsolla-billed sub can't be
 *   replaced from our Stripe flow, so those users are ineligible.
 */
export const useTrialEligibility = () => {
  const { allstarUser } = useUser();
  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  // Unlimited-only users buy outright: gated here, not per surface, or they
  // would just start trials from the header instead. Only FREE never-
  // subscribed users are affected; past subscribers and paying tiers keep the
  // full grid, so they keep the trial too.
  const { unlimitedOnly: unlimitedOnlyActive } = useUnlimitedOnlyPaywall();

  const user = allstarUser?.user;
  const currentTier = user?.userGroup?.type ?? "FREE";
  const isFree = currentTier === "FREE";

  const stripeBilled =
    (user?.credits?.providerName ?? "STRIPE") === "STRIPE" &&
    !user?.usingAppStoreInAppPurchases &&
    !user?.usingPlayStoreInAppPurchases;
  const tierCanTrial =
    isFree || (!TRIAL_INELIGIBLE_TIERS.includes(currentTier) && stripeBilled);

  const isTrialEligible =
    showSubscriptionOverhaul &&
    !unlimitedOnlyActive &&
    !user?.userGroup?.hasUsedPlatinumTrial &&
    tierCanTrial;

  // Paying users check out with a card and the trial converts at day 7 —
  // surfaces gated on this must not promise "no card required".
  const requiresCard = isTrialEligible && !isFree;

  return { isTrialEligible, requiresCard, currentTier };
};
